import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * DashboardAlerts — shows actionable alert items from the API.
 *
 * Props:
 *   alerts: Array<{ type, severity, message, count, action_url }>
 *   loading: bool
 *
 * Alerts now come directly from the /dashboard/overview API response
 * (computed server-side) rather than being built client-side.
 */

const SEVERITY_META = {
  critical: { bg: '#FEF2F2', border: '#EF4444', icon: '🔴', btnClass: 'btn-danger' },
  warning:  { bg: '#FFFBEB', border: '#F59E0B', icon: '🟡', btnClass: 'btn-warning' },
  info:     { bg: '#EFF6FF', border: '#3B82F6', icon: '🔵', btnClass: 'btn-info' },
};

const AlertItem = ({ alert, onAction }) => {
  const meta = SEVERITY_META[alert.severity] || SEVERITY_META.info;
  return (
    <div
      className="alert-item"
      style={{ borderLeftColor: meta.border, background: meta.bg }}
    >
      <span className="alert-item__icon">{meta.icon}</span>
      <div className="alert-item__content">
        <span className="alert-item__message">{alert.message}</span>
        {alert.count > 0 && (
          <span className="alert-item__count badge badge--neutral">{alert.count}</span>
        )}
      </div>
      {alert.action_url && (
        <button
          className="alert-item__action btn-link"
          onClick={() => onAction(alert.action_url)}
        >
          View →
        </button>
      )}
    </div>
  );
};

const AlertsSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {[1, 2].map(i => (
      <div key={i} className="skeleton" style={{ height: 48, borderRadius: 10 }} />
    ))}
  </div>
);

const DashboardAlerts = ({ alerts = [], loading }) => {
  const navigate = useNavigate();

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;

  return (
    <div className="dashboard-section">
      <div className="dashboard-section__header">
        <h3 className="dashboard-section__title">
          Alerts
          {criticalCount > 0 && (
            <span className="badge badge--danger" style={{ marginLeft: 8 }}>
              {criticalCount} critical
            </span>
          )}
        </h3>
        {alerts.length > 3 && (
          <button className="btn-link" onClick={() => navigate('/inventory')}>
            View all
          </button>
        )}
      </div>

      {loading ? (
        <AlertsSkeleton />
      ) : alerts.length === 0 ? (
        <div className="empty-state empty-state--inline">
          <span style={{ fontSize: 20 }}>✅</span>
          <span>All good — no items need attention right now.</span>
        </div>
      ) : (
        <div className="alerts-list">
          {alerts.slice(0, 5).map((alert, idx) => (
            <AlertItem
              key={`${alert.type}-${idx}`}
              alert={alert}
              onAction={(path) => path && navigate(path)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardAlerts;
